import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import useFileUploadStore from "../../../../stores/fileUploadStore";

const RESUME_UI_STATE_MAP = {
  PENDING_VALIDATION_ERRORS: "validation-errors",
  PENDING_APPROVAL: "ready-for-approval",
};
import { useQueryClient } from "@tanstack/react-query";
import { useUploadBulkFile, bulkUploadsQueryKeys } from "../../../../hooks/queries/useBulkUploadsQueries";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import { toastService } from "../../../../providers";
import { TransactionQueryKeys } from "../../../../hooks/queries/useTransactionQueries";

export function useFileUploadFlow() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { uiState, disbursementId, batchData } = useFileUploadStore(
    useShallow((s) => ({
      uiState: s.uiState,
      disbursementId: s.disbursementId,
      batchData: s.batchData,
    })),
  );

  const { mutate: uploadFile } = useUploadBulkFile();

  // A persisted "uploading" state can only be leftover from a refresh mid-
  // request — a live upload never has time to be observed mid-flight by a
  // fresh mount of this hook. There's no id yet at this point to verify
  // against the backend, so self-heal back to idle instead of leaving the
  // user stuck behind a permanent full-screen loading overlay.
  useEffect(() => {
    if (useFileUploadStore.getState().uiState === "uploading") {
      useFileUploadStore.getState().resetUpload();
    }
  }, []);

  const handleUpload = (file) => {
    const formData = new FormData();
    formData.append("file", file);

    useFileUploadStore.getState().setUploading();

    uploadFile(formData, {
      onSuccess: (data) => {
        if (data?.success !== true) {
          useFileUploadStore.getState().resetUpload();
          toastService.error(data?.error || "Upload failed. Please try again.");
          return;
        }
        queryClient.invalidateQueries({ queryKey: bulkUploadsQueryKeys.all });
        queryClient.invalidateQueries({ queryKey: TransactionQueryKeys.all });
        useFileUploadStore.getState().setValidating(data.id);
      },
      onError: (error) => {
        useFileUploadStore.getState().resetUpload();
        toastService.error(
          extractAxiosErrorMessage(error, "Upload failed. Please try again."),
        );
      },
    });
  };

  const handleCancel = () => {
    useFileUploadStore.getState().reset();
    navigate("/bulkpayments");
  };

  const handleResume = (row) => {
    const resolvedUiState = RESUME_UI_STATE_MAP[row.status];
    if (!resolvedUiState) return;

    useFileUploadStore.getState().resume(row.id, resolvedUiState, {
      paymentChannel: row.paymentChannel,
      recipientCount: row.recipientCount,
      batchId: row.batchId,
    });
    navigate("/bulkpayments/file-upload");
  };

  return {
    uiState,
    disbursementId,
    batchData,
    handleUpload,
    handleCancel,
    handleResume,
  };
}
